import React, { useEffect, useState } from "react";
import { Modal, Select, Spin, message } from "antd";
import { userService, propertyService } from "@iso/services";

const { Option } = Select;

/**
 * Modal for assigning a property user to one or more files.
 *
 * Pass either `propertyId` (property-scope library) or `corporateGroupId`
 * (corporate-scope library) — never both. In corporate mode the picker
 * collects users across every property in the group.
 *
 * For single-file mode pass `initialUserId` so the picker is preselected.
 */
const AssignUserModal = ({
  visible,
  propertyId = null,
  corporateGroupId = null,
  initialUserId = null,
  onCancel,
  onConfirm,
  confirmLoading = false,
  fileCount = 1,
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(initialUserId || undefined);

  useEffect(() => {
    if (!visible) return;
    setSelectedId(initialUserId || undefined);

    if (corporateGroupId) {
      setLoading(true);
      // Resolve the property IDs in the corporate group, then pull users
      // across all of them. Two calls because there is no single
      // "users-for-corporate-group" endpoint today.
      propertyService
        .getAllProperties()
        .then((propsRes) => {
          const properties = propsRes?.properties || [];
          const ids = properties
            .filter((p) => p.corporate_group_id === corporateGroupId)
            .map((p) => p.id);
          if (ids.length === 0) {
            setUsers([]);
            return null;
          }
          return propertyService.getUsersFromMultipleProperties(ids);
        })
        .then((res) => {
          if (res && res.code === "0000" && Array.isArray(res.users)) {
            // Dedupe — a user can belong to several properties in the group.
            const seen = new Set();
            const deduped = [];
            for (const u of res.users) {
              if (!seen.has(u.id)) {
                seen.add(u.id);
                deduped.push(u);
              }
            }
            setUsers(deduped);
          } else if (res !== null) {
            setUsers([]);
          }
        })
        .catch(() => {
          message.error("Failed to load corporate group users");
          setUsers([]);
        })
        .finally(() => setLoading(false));
      return;
    }

    if (!propertyId) {
      setUsers([]);
      return;
    }
    setLoading(true);
    userService
      .getUserForProperty(propertyId)
      .then((res) => {
        // Backend returns { code, users }; verify the success code before
        // reading the array so a failure response does not silently render
        // an empty list as if there were no users.
        if (res && res.code === "0000" && Array.isArray(res.users)) {
          setUsers(res.users);
        } else {
          setUsers([]);
        }
      })
      .catch(() => {
        message.error("Failed to load property users");
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, [visible, propertyId, corporateGroupId, initialUserId]);

  const handleOk = () => {
    if (!selectedId) {
      // Unassign isn't supported yet (see backend AttachmentController C2 TODO).
      return;
    }
    onConfirm(selectedId);
  };

  const formatLabel = (user) => {
    const name = `${user.name || ""} ${user.surname || ""}`.trim();
    return name || user.email || `User #${user.id}`;
  };

  return (
    <Modal
      title={
        fileCount > 1
          ? `Assign user to ${fileCount} files`
          : "Assign user to file"
      }
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Assign"
      okButtonProps={{ disabled: !selectedId }}
      confirmLoading={confirmLoading}
      destroyOnClose
      zIndex={1200}
      wrapClassName="file-manager-bulk-modal"
    >
      <Spin spinning={loading}>
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder="Select a property user"
          value={selectedId}
          onChange={(value) => setSelectedId(value)}
          dropdownStyle={{ zIndex: 1300 }}
          filterOption={(input, option) =>
            option?.children?.toString().toLowerCase().includes(input.toLowerCase())
          }
        >
          {users.map((user) => (
            <Option key={user.id} value={user.id}>
              {formatLabel(user)}
            </Option>
          ))}
        </Select>
      </Spin>
      <div style={{ marginTop: 12, fontSize: 12, color: "#888" }}>
        Pick a property user — they will be assigned to{" "}
        {fileCount > 1 ? `all ${fileCount} selected files` : "this file"}.
      </div>
    </Modal>
  );
};

export default AssignUserModal;
